import { _ } from "../../core/polyglot/polyglot.js";
import {
	bookmarkIcon,
	bookmarkRegularIcon,
	lockIcon,
} from "../../core/elements/icons/index.js";
import { widget } from "@foretagsplatsen/widgetjs";
import jQuery from "jquery";
import SearchFieldWidget from "../../core/widgets/SearchFieldWidget/SearchFieldWidget.js";
import TreeWidget from "../../core/widgets/tree/TreeWidget/TreeWidget.js";

/**
 * Widget rendering a chart of accounts tree with a search field.
 *
 * @param {chartOfAccounts} spec.chartOfAccounts - Chart of account to display
 */
export default widget.subclass((that, my) => {
	my.initialize = function (spec) {
		my.super(spec);

		my.chartOfAccounts = spec.chartOfAccounts;
	};

	my.initializeSubwidgets = function () {
		my.super(...arguments);

		my.tree = new TreeWidget({
			data: my.getTopLevelGroups(),
			cssClass: "stripped",
			searchFn: my.searchFn,
			getChildren: my.getChildren,
			getLabel: my.getLabel,
			autoOpen: my.autoOpen(),
		});

		my.tree.onAttach.register(() => {
			my.setTreeHeight();
		});

		my.searchField = new SearchFieldWidget({
			placeholder: _("Filter accounts..."),
			inputId: `chartOfAccountsViewerWidget-input-${that.getId()}`,
			inputClass: "chartOfAccountsViewerWidget-input form-control",
		});

		my.searchField.applySearchOn(my.tree);

		my.tree.onSelectionChange.register((accountGroup, treeItem) => {
			const accountGroupParent =
				treeItem &&
				treeItem.getParent() &&
				treeItem.getParent().getModel();
			that.onSelectionChange.trigger(accountGroup, accountGroupParent);
		});
	};

	that.onSelectionChange = my.events.createEvent();

	/**
	 * Object representing current selection.
	 *
	 * @property {abstractAccountGroup} [group] - selected account group
	 * @property {abstractAccountGroup} [parent] - parent of the selected account group
	 */
	my.selection = {};

	that.onSelectionChange.register((group, parent) => {
		my.selection = { group, parent };
	});

	that.forceUpdateParentGroupFor = function (group, parent) {
		const roots = my.getTopLevelGroups();
		if (roots.indexOf(group) === -1) {
			my.tree.forceUpdateNodeFor(parent);
		} else {
			// root
			my.tree.setData(roots);
		}

		that.selectGroup(group);
	};

	that.selectedGroup = function () {
		return my.selection.group;
	};

	that.selectedGroupParent = function () {
		return my.selection.parent;
	};

	that.updateGroup = function () {
		my.tree.update();
	};

	that.addChild = function (parent, child) {
		my.tree.addChild(parent, child);
		that.forceUpdateParentGroupFor(child, parent);
	};

	that.removeGroup = function (group, parent) {
		my.tree.removeNode(group, parent);
	};

	that.selectGroup = function (group) {
		my.tree.selectNode(group);
	};

	that.setChartOfAccounts = function (chartOfAccounts) {
		my.chartOfAccounts = chartOfAccounts;
		my.tree.setData(my.getTopLevelGroups());
	};

	my.searchFn = function (group, searchString) {
		return group.matchString(searchString);
	};

	my.autoOpen = function () {
		return 1;
	};

	my.getLabel = function (group) {
		if (group.representsKnownGroup()) {
			return function (html) {
				html.span(
					{
						klass: "name knowngroup",
					},
					group.getName(),
					lockIcon,
				);
			};
		}

		return my.nameRenderer(group);
	};

	my.getChildren = function (group) {
		return group.getChildren();
	};

	that.renderContentOn = function (html) {
		html.div(
			{ klass: "row tree-actions" },
			html.div({ klass: "col-sm-6" }, my.searchField),
			html.div(
				{ klass: "col-sm-6 action-links" },
				html.a(
					{
						"data-test": "expand-all",
						click: () => my.tree.expandAll(),
					},
					_("expand all"),
				),
				_("or"),
				html.a(
					{
						"data-test": "collapse-all",
						click: () => my.tree.collapseAll(),
					},
					_("collapse all"),
				),
			),
		);

		html.render(my.tree);
	};

	my.nameRenderer = function (...args) {
		const group = args.shift();

		return function (html) {
			if (group.representsKnownGroup()) {
				const spanArgs = [
					{
						klass: `name${
							group.representsKnownGroup() ? " knowngroup" : ""
						}`,
					},
					group.getName(),
					html.span(
						{
							klass: "fa-stack knowngroup-icon",
						},
						bookmarkIcon({
							class: "fa-stack-1x",
						}),
						bookmarkRegularIcon({
							class: "fa-stack-1x",
						}),
					),
				];

				spanArgs.push.apply(spanArgs, args);
				html.span.apply(null, spanArgs);
			} else {
				html.render(group.getName());
			}
		};
	};

	my.didAttach = function () {
		jQuery(window).resize(my.setTreeHeight);
	};

	my.willDetach = function () {
		jQuery(window).unbind("resize", my.setTreeHeight);
	};

	my.getTopLevelGroups = function () {
		if (my.chartOfAccounts) {
			return my.chartOfAccounts.getMainTree().getChildren();
		}
		return [];
	};

	/**
	 * Setup the tree so that we always have a 200px margin on the bottom,
	 * visible on the screen (no overflow).
	 */
	my.setTreeHeight = function () {
		const totalHeight = jQuery(document).height();
		const minHeight = 200;
		const margin = 200;
		const visibleTree = jQuery(".tab-pane.active .tree-root");
		if (visibleTree.get(0)) {
			const offset = visibleTree.offset().top;
			const height = Math.max(totalHeight - offset - margin, minHeight);
			jQuery(".tab-pane .tree-root").height(height);
		}
	};
});
